'use client'
import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDown, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons'
import Logo from '../../../public/Image/logo.jpg'
import Link from 'next/link'
import Image from 'next/image'
import { useSidebar } from '@/hooks/useSidebar'
import { useRouter } from 'next/navigation'
import { HeaderProps } from './Header'

const Sidebar = ({ menus = [], adsMenus = [] }: HeaderProps) => {
  const router = useRouter()
  const { isOpen, closeSidebar } = useSidebar()
  const [activeMenu, setActiveMenu] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  // Xử lý khi submit tìm kiếm
  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (searchQuery.trim() !== '') {
      router.push(`/tim-kiem?search_q=${encodeURIComponent(searchQuery)}&cate_code=all`)
    }
  }

  const toggleSubMenu = (index: number) => {
    setActiveMenu(activeMenu === index ? null : index)
  }

  // Thêm lớp 'overflow-hidden' khi Sidebar mở
  useEffect(() => {
    if (isOpen) {
      document.documentElement.classList.add('overflow-hidden')
    } else {
      document.documentElement.classList.remove('overflow-hidden')
    }

    return () => {
      document.documentElement.classList.remove('overflow-hidden')
    }
  }, [isOpen])

  return (
    <>
      {isOpen && (
        <div
          className="fixed top-[56px] left-0 right-0 bottom-0 bg-black opacity-50 z-40 xl:hidden"
          onClick={closeSidebar}
        ></div>
      )}
      <aside
        className={`fixed z-50 top-[56px] left-0 h-full bg-white text-black w-96 transform transition-transform duration-300 xl:hidden ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <nav className="flex flex-col h-full">
          {/* 🔹 Phần Logo & Thanh Tìm Kiếm (Cố định) */}
          <div className="p-4 bg-white sticky top-0 z-50 border-b border-gray-300">
            <Link href="/" className="inline-block mb-2">
              <Image
                src={Logo}
                alt="logo-tapnews"
                width={100}
                className="w-full"
                priority
                unoptimized
              />
            </Link>
            <form className="relative w-full" onSubmit={handleSearchSubmit}>
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="p-2 pl-10 w-full border border-gray-300 rounded-full focus:outline-none text-black"
                placeholder="Tìm kiếm..."
              />
              <button
                type="submit"
                aria-label="submit button"
                className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-500"
              >
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </button>
            </form>
          </div>

          {/* 🔹 Phần Menu (Scroll riêng) */}
          <div className="flex-1 overflow-y-auto">
            <ul className="p-4 pb-96">
              {menus &&
                menus.map((item, index) => (
                  <li key={item.id} className="mb-4">
                    {Array.isArray(item.childCategories) && item.childCategories.length > 0 ? (
                      <button
                        className="flex items-center justify-between w-full text-left hover:text-primary uppercase font-medium pb-2 border-b border-gray-300"
                        aria-label="toggle button"
                        onClick={() => toggleSubMenu(index)}
                      >
                        <span>{item.name}</span>
                        <FontAwesomeIcon
                          icon={faAngleDown}
                          className={`transition-transform duration-200 ${
                            activeMenu === index ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                    ) : (
                      <Link
                        href={`/danh-muc/${item.slug}`}
                        className="block uppercase hover:text-primary font-medium transition-colors duration-200 pb-2 border-b border-gray-300"
                      >
                        {item.name}
                      </Link>
                    )}

                    <ul
                      className={`ml-4 mt-2 transition-all duration-300 ease-in-out origin-top ${
                        activeMenu === index
                          ? 'opacity-100 visible translate-y-0'
                          : 'opacity-0 invisible -translate-y-5'
                      }`}
                    >
                      {Array.isArray(item.childCategories) &&
                        item.childCategories.length > 0 &&
                        activeMenu === index &&
                        item.childCategories.map((subitem) => (
                          <li key={subitem.id}>
                            <Link
                              href={`/danh-muc/${subitem.slug}`}
                              className="block py-2 px-4 hover:bg-primary hover:text-white capitalize rounded transition-colors duration-200"
                            >
                              {subitem.name}
                            </Link>
                          </li>
                        ))}
                    </ul>
                  </li>
                ))}
              <li className="mb-4">
                <Link
                  href="https://www.youtube.com/@TAPNewsUS/videos"
                  className="flex items-center justify-between w-full text-left hover:text-primary uppercase font-medium pb-2 border-b border-gray-300"
                >
                  <span>Video</span>
                </Link>
              </li>
              {/* 🔹 Rao Vặt */}
              <li className="mb-4">
                <button
                  className="flex items-center justify-between w-full text-left hover:text-primary uppercase font-medium pb-2 border-b border-gray-300"
                  aria-label="toggle button"
                  onClick={() => toggleSubMenu(99)}
                >
                  <span>Rao Vặt</span>
                  <FontAwesomeIcon
                    icon={faAngleDown}
                    className={`transition-transform duration-200 ${
                      activeMenu === 99 ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                <ul
                  className={`ml-4 mt-2 transition-all duration-300 ease-in-out origin-top ${
                    activeMenu === 99
                      ? 'opacity-100 visible translate-y-0'
                      : 'opacity-0 invisible -translate-y-5'
                  }`}
                >
                  {Array.isArray(adsMenus) &&
                    adsMenus.length > 0 &&
                    activeMenu === 99 &&
                    adsMenus.map((subitem) => (
                      <li key={subitem.slug}>
                        <Link
                          href={`https://raovat.iamtapnews.com//danh-muc/${subitem.slug}`}
                          className="block py-2 px-4 hover:bg-primary hover:text-white capitalize rounded transition-colors duration-200"
                        >
                          {subitem.name}
                        </Link>
                      </li>
                    ))}
                </ul>
              </li>
            </ul>
          </div>
        </nav>
      </aside>
    </>
  )
}

export default Sidebar
